import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DownloadExcelButton, NewReactTable as TableComponent } from '../../ui/index.jsx';
import { request, methods, isRequestCancelled } from '../../../../../utils/http-common.js';
import useCancellableRequest from '../../../../../hooks/useCancellableRequest.js';
import { formatNumberWithCommas } from '../../../../../helpers/numericHelpers.js';
import { WmapeSummaryTableSkeleton } from '../../common/ModulePageSkeleton/ModulePageSkeleton.jsx';
import { MONTH_KEY_RE } from '../Insights/shared.jsx';

const WMAPE_API_PAYLOAD = {
  view: 'wmape-summary',
  radio_nsr: 'volume',
};

const RATING_COLORS = {
  P90: { bg: '#e8f5e9', color: '#2e7d32' },
  P70: { bg: '#fff8e1', color: '#f57f17' },
  P50: { bg: '#fce4ec', color: '#c62828' },
};

const buildWmapeColumns = (headers) =>
  (headers || []).map((h) => {
    const isNumeric = h.data_type === 'number' || MONTH_KEY_RE.test(h.key);
    const isCategory = h.key === 'category';

    return {
      key: h.key,
      id: h.key,
      accessor: h.key,
      Header: () => (
        <span className={isNumeric ? 'flex jc-center' : ''} style={{ fontWeight: isCategory ? 600 : 400 }}>
          {h.label}
        </span>
      ),
      width: isCategory ? 180 : 96,
      sortable: false,
      filterable: false,
      Cell: ({ row }) => {
        const val = row.original[h.key];
        const group = row.original.group;
        const category = row.original.category;

        if (isCategory) {
          const riskStyle = RATING_COLORS[category];
          return (
            <span
              style={{
                fontWeight: group === 'total' ? 600 : 500,
                ...(riskStyle
                  ? {
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: riskStyle.bg,
                      color: riskStyle.color,
                    }
                  : {}),
              }}
            >
              {val ?? '—'}
            </span>
          );
        }

        if (val === null || val === undefined || val === '') {
          return <span className="flex jc-center" style={{ color: '#aaa' }}>—</span>;
        }

        const num = typeof val === 'number' ? val : Number(val);
        if (Number.isNaN(num)) {
          return <span className="flex jc-center">{val}</span>;
        }

        return (
          <span className="flex jc-center" style={{ fontWeight: group === 'total' ? 600 : 400 }}>
            {`${formatNumberWithCommas(num)}%`}
          </span>
        );
      },
    };
  });

export default function WMAPEView({ handleDisableTab }) {
  const { request: cancellableRequest } = useCancellableRequest('sales-pnf/forecast-report/wmape');

  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    let requestError;
    try {
      let res;
      [requestError, res] = await cancellableRequest(
        'salespnf/getlightgbmwmapesummary',
        methods.POST,
        WMAPE_API_PAYLOAD,
        { cancelKey: 'wmape' },
      );
      if (isRequestCancelled(requestError)) return;
      if (requestError) {
        setFetchError(requestError.message || 'Failed to load WMAPE summary');
        setHeaders([]);
        setRows([]);
        return;
      }
      if (res?.data) {
        setHeaders(res.data.row_header || []);
        setRows(res.data.row_data || []);
      }
    } catch (e) {
      if (!isRequestCancelled(e)) {
        console.error('Failed to fetch WMAPE summary', e);
        setFetchError('Failed to load WMAPE summary');
      }
    } finally {
      if (!isRequestCancelled(requestError)) {
        setLoading(false);
      }
    }
  }, [cancellableRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    handleDisableTab(loading);
  }, [loading, handleDisableTab]);

  useEffect(() => () => {
    handleDisableTab(false);
  }, [handleDisableTab]);

  const handleExport = useCallback(async () => {
    if (isExporting || !rows.length) return;
    setIsExporting(true);
    try {
      const [, response] = await request(
        'salespnf/getlightgbmwmapesummary',
        methods.POST,
        { ...WMAPE_API_PAYLOAD, export: 'true', export_label: 'WMAPE' },
        { responseType: 'blob' },
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers?.['content-disposition'] || '';
      a.download = disposition.split('=').slice(1).join('=') || 'WMAPE_Summary.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('WMAPE export failed', e);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, rows.length]);

  const columns = useMemo(() => buildWmapeColumns(headers), [headers]);

  const exportToolbar = (
    <Box display="flex" alignItems="center" width="100%" mb={1}>
      <Box flex={1} />
      <DownloadExcelButton
        onClick={handleExport}
        loading={isExporting}
        disabled={loading || !rows.length}
      />
    </Box>
  );

  const tableData = useMemo(
    () => rows.map((row, index) => ({
      ...row,
      _myId: `wmape-${index}-${row.category}`,
    })),
    [rows],
  );

  if (loading) {
    return (
      <Box className="wmape-summary-view">
        {exportToolbar}
        <WmapeSummaryTableSkeleton />
      </Box>
    );
  }

  return (
    <Box className="wmape-summary-view">
      {exportToolbar}
      {fetchError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {fetchError}
        </Typography>
      )}

      {!fetchError && rows.length === 0 && (
        <Typography variant="body2" sx={{ color: '#888', py: 4, textAlign: 'center' }}>
          No WMAPE data available
        </Typography>
      )}

      {rows.length > 0 && (
        <div className="jklc-table-wrapper forecast-report-table wmape-summary-table">
          <TableComponent
            columns={columns}
            data={tableData}
            hidePagination
            stickyColumnCount={1}
          />
        </div>
      )}
    </Box>
  );
}
